import {assertAndContinue} from 'Asserts';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  StatusTypeToDisplayNameMapping,
  Task,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';
import {
  PMEIsQuery_procedureMethodExecutionInstances as PMEIOrInaccessible,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask as PMEIMethodTask,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask_ProcessTaskNode_statusChanges_ProcessTaskStatusChangeNode as StatusChangeNode,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_taskTransitionExtensions_TaskTransitionExtensionNode as TaskTransitionExtensionNode,
} from 'procedures/worksheets/__generated/PMEIsQuery';
import {HasAvatar} from 'ui-library/avatar/Avatar';

interface StatusChangeInfo {
  creator?: HasAvatar;
  timestamp: string;
}

function extractStatusChangeInfo(
  statusType: WorksheetTaskStatusType,
  statusChanges: Array<StatusChangeNode>,
  taskTransitionExtensions: Array<TaskTransitionExtensionNode>
): StatusChangeInfo | null {
  const creator = statusChanges.find(
    (statusChange) => statusChange.newStatus.displayName === StatusTypeToDisplayNameMapping[statusType]
  )?.creator;

  const timestamp = taskTransitionExtensions.find(
    (ext) => ext.statusDisplayName === StatusTypeToDisplayNameMapping[statusType]
  )?.timestampUtcIsoformat;

  return timestamp ? {creator, timestamp} : null;
}

export function getProcedureType(pmeis?: Array<PMEIOrInaccessible>): ProcedureType | undefined {
  if (!pmeis || !pmeis[0] || isInaccessibleInfo(pmeis[0])) {
    return undefined;
  }
  const allEqual = pmeis.every(
    (pmei) =>
      !isInaccessibleInfo(pmei) &&
      !isInaccessibleInfo(pmeis[0]) &&
      pmei.procedureRun.procedureType === pmeis[0].procedureRun.procedureType
  );
  assertAndContinue(allEqual, 'All PMEIs should have the same procedure type');
  return pmeis[0].procedureRun.procedureType;
}

export function extractProcedureRunDataFromTask(
  task?: PMEIMethodTask,
  pmei?: PMEIOrInaccessible
): {
  unitOperationName?: string;
  replicateNumber?: number;
  conditionName?: string;
  runStarted?: StatusChangeInfo | null;
  runEnded?: StatusChangeInfo | null;
  procedureType?: ProcedureType;
} {
  if (!task || isInaccessibleInfo(task) || !pmei || isInaccessibleInfo(pmei)) {
    return {};
  }

  const {statusChanges} = task;
  const {planMethodDefinitionVersion, procedureRunConditionReplicate, procedureRun} = pmei;

  const runStarted = extractStatusChangeInfo(
    WorksheetTaskStatusType.IN_PROGRESS,
    statusChanges,
    pmei.taskTransitionExtensions
  );
  const runEnded = extractStatusChangeInfo(
    WorksheetTaskStatusType.COMPLETED,
    statusChanges,
    pmei.taskTransitionExtensions
  );

  return {
    unitOperationName: planMethodDefinitionVersion.name,
    replicateNumber: procedureRunConditionReplicate.replicateNumber,
    conditionName: procedureRunConditionReplicate.procedureRunCondition.name,
    runStarted,
    runEnded,
    procedureType: procedureRun.procedureType,
  };
}

export function areAllMethodTasksCreated(pmeis: Array<PMEIOrInaccessible>, tasks: Array<Task>): boolean {
  const taskIds = tasks
    .map((task) => (task.__typename === 'ProcessTaskNode' ? task.id : ''))
    .filter((id) => id);
  return (
    (pmeis.length === 0 && tasks.length !== 0) ||
    (pmeis.length === tasks.length &&
      tasks.length !== 0 &&
      pmeis.every(
        (pmei) =>
          pmei.__typename === 'ProcedureMethodExecutionInstanceNode' &&
          pmei.methodTask &&
          pmei.methodTask.__typename === 'ProcessTaskNode' &&
          taskIds.includes(pmei.methodTask.id)
      ))
  );
}
